import {
  Checkbox,
  Chip,
  Divider,
  FormControl,
  Grid,
  MenuItem,
  Select,
  SelectChangeEvent,
  Tooltip,
  Typography,
} from "@mui/material";
import moment from "moment";
import React, { ChangeEvent, FC, useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  clearDispatchFeed,
  setFeedArea,
  updateUserPreferencesRequest,
} from "../../../../actions";
import { DispatchEventFeedModel, IRootReducer } from "../../../../reducers";
import CreateIncident from "../../../incident-model/create-incident-model";
import MoreActionsMenu from "../../more-actions-menu";
import PinEventModal from "../../pin-event-modal";
import AgencyFilters from "../agency-filters";
import { getWspAreaParamValue, WspAgencyLabel, AreaCode, AgencyCode } from "../../../../reducers/states/agency";

interface IDFGridHeaderProps {
  props: {
    mostRecentSelectedEvent: DispatchEventFeedModel;
    selectedIDFEvents: DispatchEventFeedModel[];
    selectedPinnedEvents: DispatchEventFeedModel[];
    isDashDisplay?: boolean;
    isSideDisplay?: boolean;
  };
}

const IDFGridHeader: FC<IDFGridHeaderProps> = ({ props }) => {
  const dispatch = useDispatch();
  const [currentDatetime, setCurrentDateTime] = useState(moment().format("LL, HH:mm:ss"));
  const userPrefStore = useSelector((state: IRootReducer) => state.userInfoReducer.userPreferences);

  const hasDispatched = useRef(false);  
  useEffect(() => {
    if(userPrefStore?.agencyFilters && !hasDispatched.current) {
      // This is meant to be the initial setting of this value and meant to be done only once from here.
      const areaFilters = getAreaParamValues(userPrefStore.agencyFilters);
      dispatch(setFeedArea(areaFilters));
      hasDispatched.current = true;
    }    
  }, [dispatch, userPrefStore.agencyFilters]);

  useEffect(() => {
    let secTimer = setInterval(() => {
      setCurrentDateTime(moment().format("LL, HH:mm:ss"));
    }, 1000);

    return () => clearInterval(secTimer);
  }, []);

  const handleTimeChange = (event: SelectChangeEvent<number>) => {
    dispatch(updateUserPreferencesRequest({
      ...userPrefStore,
      timeFrame: event.target.value as number,
    }));
  };

  const handleViewChange = (event: SelectChangeEvent<string>) => {
    dispatch(clearDispatchFeed());
    dispatch(updateUserPreferencesRequest({
      ...userPrefStore,
      feedView: event.target.value as string,
    }));
  };

  const handleShowOpenOnly = (event: any) => {
    dispatch(updateUserPreferencesRequest({
      ...userPrefStore,
      showOpenDispatchesOnly: event.target.checked as boolean,
    }));
  };

  const getAreaParamValues = (updatedAgencies: string[]): string => {
    const areaFilterValues: string[] = [];
    updatedAgencies.forEach((agency) => {
      let value = getWspAreaParamValue(agency);
      if (value) {
        areaFilterValues.push(value);
      }

      if (agency === WspAgencyLabel.SEA) {
        // We're still saving a subset of the D2 events with area value "I5" instead of "D2",
        // so we need to use both values to get all SEA events.
        areaFilterValues.push(AreaCode.I5);
      }
    });

    return areaFilterValues.join(",");
  };

  const handleAgencySelect = (event: ChangeEvent<HTMLInputElement>) => {
    const agency = event.target.name;
    let updatedFilters = [...userPrefStore.agencyFilters];

    if (event.target.checked) {
      updatedFilters.push(agency);
    } else {
      let index = updatedFilters.indexOf(agency);
      if (index > -1) updatedFilters.splice(index, 1);
    }

    // Update area if WSP filters changed
    if (agency.startsWith(AgencyCode.WSP)) {
      // this is where the areaParamValues are mapped.
      const updatedAreaFilters = getAreaParamValues(updatedFilters);
      dispatch(setFeedArea(updatedAreaFilters));
    }

    dispatch(
      updateUserPreferencesRequest({
        ...userPrefStore,
        agencyFilters: updatedFilters,
      }),
    );
  };

  return (
    <Grid container alignItems={"center"} alignContent={"center"} justifyContent={"left"} p={1}>
      <Grid item container xs={5}>
        <Typography variant="subtitle1">{currentDatetime}</Typography>
      </Grid>
      <Grid item container xs={7} alignItems="top" justifyContent="right" wrap="nowrap" gap={1}>
        <Grid item>
          <CreateIncident
            props={{
              mostRecentSelectedEvent: props.mostRecentSelectedEvent,
              selectedEvents: [...props.selectedIDFEvents, ...props.selectedPinnedEvents]
            }}
          />
        </Grid>
        <Grid item>
          <PinEventModal
            props={{
              selectedIDFEvents: props.selectedIDFEvents,
              selectedPinnedEvents: props.selectedPinnedEvents,
            }}
          />
        </Grid>
        <Grid item>
          <MoreActionsMenu
            props={{
              isDashDisplay: props.isDashDisplay,
            }}
          />
        </Grid>
      </Grid>
      <Grid item xs={12} pt={1} pb={1}>
        <Divider />
      </Grid>
      <Grid item>
        <AgencyFilters
          props={{
            agencyFilters: userPrefStore.agencyFilters || [],
            handleAgencySelect: handleAgencySelect,
          }}
        />
      </Grid>
      <Grid item>
        <Tooltip title="Displays only open dispatch events when toggled on">
          <Checkbox
            name="Open Only"
            checked={!!userPrefStore.showOpenDispatchesOnly}
            onChange={handleShowOpenOnly}
            checkedIcon={<Chip variant="filled" label="Open Only" color="primary" />}
            icon={<Chip variant="outlined" label="Open Only" />}
            sx={{ padding: "5px" }}
          />
        </Tooltip>
      </Grid>
      <Grid item>
        <Typography variant="body1" mr={1}>Time:</Typography>
      </Grid>
      <Grid item>
        <FormControl>
          <Select
            value={userPrefStore.timeFrame || 5}
            onChange={handleTimeChange}
            variant="standard"
          >
            <MenuItem value={5} data-rum-id="timeframe-5min">5 minutes</MenuItem>
            <MenuItem value={10} data-rum-id="timeframe-10min">10 minutes</MenuItem>
            <MenuItem value={30} data-rum-id="timeframe-30min">30 minutes</MenuItem>
            <MenuItem value={60} data-rum-id="timeframe-1hr">1 hour</MenuItem>
            <MenuItem value={120} data-rum-id="timeframe-2hr">2 hours</MenuItem>
          </Select>
        </FormControl>
      </Grid>
      <Grid item>
        <Typography variant="body1" m={1}>View:</Typography>
      </Grid>
      <Grid item>
        <FormControl>
          <Select
            value={userPrefStore?.feedView || "All"}
            onChange={handleViewChange}
            variant="standard"
          >
            <MenuItem data-rum-id="feed-view-all" value={"All"}>All</MenuItem>
            <MenuItem data-rum-id="feed-view-noteworthy" value={"Noteworthy"}>Noteworthy</MenuItem>
          </Select>
        </FormControl>
      </Grid>
    </Grid>
  );
};

export default IDFGridHeader;
